/**
 * Anmeldung ans Backoffice.
 *
 * E-Mail und Passwort — nicht der Einrichtungscode aus der App. Der Code ist
 * dafür gebaut, ein GERÄT einmalig an einen Fahrer zu binden; im Büro sitzen
 * mehrere Personen an wechselnden Rechnern, und die Anmeldung muss wiederholbar
 * sein.
 *
 * Bewusst kein „Passwort vergessen" per E-Mail: Der Standardversand von
 * Supabase ist streng begrenzt und für einen Betrieb nicht verlässlich. Ein
 * Knopf, der eine Mail verspricht, die dann nicht ankommt, ist schlimmer als
 * gar kein Knopf. Zurücksetzen macht deshalb ein Konto mit Adminrechten im
 * Backoffice.
 */

import { useState, type FormEvent } from 'react';
import { useSitzung } from './Sitzung';
import { Knopf, Meldung, Textfeld } from '../gestaltung/bausteine';

export function AnmeldenSeite() {
  const { anmelden } = useSitzung();
  const [email, setEmail] = useState('');
  const [passwort, setPasswort] = useState('');
  const [laeuft, setLaeuft] = useState(false);
  const [fehler, setFehler] = useState<string | null>(null);

  async function absenden(ereignis: FormEvent) {
    ereignis.preventDefault();
    if (laeuft) return;

    setFehler(null);
    setLaeuft(true);
    try {
      await anmelden(email, passwort);
      // Kein Weiterleiten nötig: sobald die Sitzung steht, wechselt der
      // Anbieter auf 'bereit' und App.tsx zeigt den Rahmen.
    } catch (problem) {
      const text = (problem as { message?: string })?.message ?? '';
      // Supabase antwortet auf falsches Passwort und unbekannte Adresse
      // absichtlich gleich, damit man Konten nicht durchprobieren kann. Diese
      // Zurückhaltung geben wir so weiter, statt sie auszuplaudern.
      if (text.includes('Invalid login credentials')) {
        setFehler('E-Mail-Adresse oder Passwort stimmt nicht.');
      } else if (text.includes('Email not confirmed')) {
        setFehler(
          'Die E-Mail-Adresse ist noch nicht bestätigt. Das muss im Backoffice freigeschaltet werden.',
        );
      } else if (
        text.includes('Failed to fetch') ||
        text.includes('NetworkError')
      ) {
        setFehler(
          'Keine Verbindung zum Server. Prüfe die Internetverbindung.',
        );
      } else {
        setFehler(text || 'Die Anmeldung hat nicht geklappt.');
      }
      setLaeuft(false);
    }
  }

  return (
    <div className="anmelden">
      <form className="anmelden__karte" onSubmit={absenden}>
        <div className="anmelden__marke">
          <span>Haid &amp; Jerg</span>
          <h1>Backoffice</h1>
        </div>

        {fehler && <Meldung art="fehler">{fehler}</Meldung>}

        <Textfeld
          marke="E-Mail-Adresse"
          wert={email}
          setzen={setEmail}
          art="email"
          autoComplete="username"
          autoFocus
          disabled={laeuft}
        />

        <Textfeld
          marke="Passwort"
          wert={passwort}
          setzen={setPasswort}
          art="password"
          autoComplete="current-password"
          disabled={laeuft}
        />

        <Knopf art="haupt" type="submit" disabled={laeuft || !email || !passwort}>
          {laeuft ? 'Wird geprüft …' : 'Anmelden'}
        </Knopf>

        <p className="meta">
          Passwort vergessen? Ein Zugang mit Adminrechten setzt es unter
          „Mitarbeiter" neu. Fahrer melden sich nicht hier an, sondern mit dem
          Einrichtungscode in der App.
        </p>
      </form>
    </div>
  );
}
